import jStat from 'jstat';
import type { BinomialParams, ChiSquareParams, NormalParams, PoissonParams } from './types';

/**
 * Calculates probability using binomial distribution
 * @param params Object containing successes, trials, and probability
 * @returns Object containing PDF and CDF values
 */
export function calculateBinomialDistribution({ successes, trials, probability }: BinomialParams): { pdf: number; cdf: number } {
  if (probability < 0 || probability > 1) throw new Error('Probability must be between 0 and 1');
  if (successes < 0 || trials < 0) throw new Error('successes and trials must be non-negative');
  if (successes > trials) throw new Error('successes cannot be greater than trials');
  
  return {
    pdf: jStat.binomial.pdf(successes, trials, probability),
    cdf: jStat.binomial.cdf(successes, trials, probability)
  };
}

/**
 * Calculates chi-square test statistic and p-value
 * @param params Object containing observed and expected frequencies
 * @returns Object containing test statistic and p-value
 */
export function calculateChiSquare({ observed, expected }: ChiSquareParams): { statistic: number; pValue: number } {
  if (observed.length === 0 || expected.length === 0 || observed.length !== expected.length) {
    throw new Error('Invalid input arrays');
  }

  // Calculate chi-square statistic
  const statistic = observed.reduce((sum, obs, i) => {
    const exp = expected[i];
    if (exp === undefined || exp === 0) throw new Error('Expected frequency cannot be zero');
    return sum + Math.pow(obs - exp, 2) / exp;
  }, 0);

  // Degrees of freedom
  const df = observed.length - 1;

  // Calculate p-value using chi-square distribution
  const pValue = 1 - jStat.chisquare.cdf(statistic, df);

  return { statistic, pValue };
}

/**
 * Calculates normal distribution probabilities and points for visualization
 * @param params Object containing mean, standard deviation, and value
 * @returns Object containing probabilities and visualization data
 */
export function calculateNormalDistribution({ mean, standardDeviation, value, numPoints = 100 }: NormalParams): {
  pdf: number[];
  cdf: number[];
  xValues: number[];
  probabilityLessThan: number;
  probabilityGreaterThan: number;
  zScore: number;
  value: number;
  mean: number;
  standardDeviation: number;
} {
  if (standardDeviation <= 0) {
    throw new Error('Standard deviation must be positive');
  }

  // Generate points for the normal distribution curve
  const range = standardDeviation * 4; // Show ±4 standard deviations
  const xValues: number[] = [];
  const pdf: number[] = [];
  const cdf: number[] = [];
  
  for (let i = 0; i < numPoints; i++) {
    const x = mean - range + (2 * range * i) / (numPoints - 1);
    xValues.push(x);
    pdf.push(jStat.normal.pdf(x, mean, standardDeviation));
    cdf.push(jStat.normal.cdf(x, mean, standardDeviation));
  }

  // Calculate z-score and probabilities
  const zScore = (value - mean) / standardDeviation;
  const probabilityLessThan = jStat.normal.cdf(value, mean, standardDeviation);
  const probabilityGreaterThan = 1 - probabilityLessThan;

  return {
    pdf,
    cdf,
    xValues,
    probabilityLessThan,
    probabilityGreaterThan,
    zScore,
    value,
    mean,
    standardDeviation
  };
}

/**
 * Calculates probability using Poisson distribution
 * @param params Object containing events and lambda (mean rate)
 * @returns Object containing PDF and CDF values
 */
export function calculatePoissonDistribution({ events, lambda }: PoissonParams): { pdf: number; cdf: number } {
  if (lambda <= 0) throw new Error('Lambda (mean rate) must be positive');
  if (events < 0) throw new Error('Number of events must be non-negative');
  if (!Number.isInteger(events)) throw new Error('Number of events must be an integer');
  
  try {
    const pdf = jStat.poisson.pdf(events, lambda);
    const cdf = jStat.poisson.cdf(events, lambda);
    
    // Handle potential NaN or Infinity values
    if (!isFinite(pdf) || !isFinite(cdf)) {
      throw new Error('Calculation resulted in non-finite values');
    }
    
    return { pdf, cdf };
  } catch (error) {
    console.error('Error in Poisson calculation:', error);
    throw error;
  }
} 